/**
 * AR Vision Demo — bulk statement-send seed ("approve 25 payment reminders").
 *
 * THROWAWAY DEMO CODE. Builds the `bulkStatement` slice: a batch of 25
 * overdue customers, each with the per-customer payloads the REAL
 * `SendStatementDialog` consumes through the AR statement hook shims —
 * `previewByCustomer` (subject/body/invoices/templateVariables; the PDF is
 * rendered on demand from `invoices`, so none is stored), `defaultsByCustomer`
 * (recipients + send options), and `contactsByCustomer` (the recipient picker).
 *
 * Everything is hardcoded and deterministic so a reset re-seeds the exact same
 * batch. Money is decimal strings, timestamps ISO. Delete alongside
 * frontend/src/demo/ar.
 */

import type {
  ArCustomerContact,
  ArInvoiceListItem,
  ArStatementEmailPreviewOutput,
  GetArAgingResponse,
  GetArCustomAgingResponse,
} from '@erp/rpc';
import type { DemoBulkStatementState } from './fixture-store.types';

/** Our org (the sender on the statement letterhead) — matches the vision demo. */
const ORG_NAME = 'Gently';
const SENDER_EMAIL = 'ar@gently-demo.com';

/** The inbox task this batch maps to (also seeded into `agentActions`). */
export const BULK_STATEMENT_ACTION_ID = 'demo-action-statement-bulk-25';

// Small local date helpers (kept local so this seed stays a deletable unit).
function isoDaysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}
function dateOnlyDaysFromNow(days: number): string {
  return isoDaysFromNow(days).slice(0, 10);
}

/** Format a decimal string as USD for body/template copy (e.g. "12,480.00" → "$12,480.00"). */
function usd(decimal: string): string {
  const n = Number(decimal);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

// ---------------------------------------------------------------------------
// The 25-customer cast. Distinct from the vision/technical-demo cast (Harbor
// Foods, Cascade, Granite, Crestline) so the batch reads as "the rest of the book".
// `apName`/`apEmail` drive the recipient picker; `code` drives invoice numbers
// and the PDF filename; `domain` keeps emails plausible.
// ---------------------------------------------------------------------------
interface BulkCustomerSpec {
  name: string;
  code: string;
  domain: string;
  apName: string;
  /** How many open invoices this customer carries (1–4, deterministic). */
  invoiceCount: number;
  /** Days since this customer's last statement; null = never sent (first reminder). */
  lastStatementDaysAgo: number | null;
}

const CUSTOMERS: BulkCustomerSpec[] = [
  {
    name: 'Northwind Traders',
    code: 'NWND',
    domain: 'northwind-traders.com',
    apName: 'Bianca Ortiz',
    invoiceCount: 2,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Summit Ridge Supply',
    code: 'SMRT',
    domain: 'summitridgesupply.com',
    apName: 'Owen Castellano',
    invoiceCount: 3,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Harbor Point Industrial',
    code: 'HRBR',
    domain: 'harborpoint-ind.com',
    apName: 'Maya Lindqvist',
    invoiceCount: 1,
    lastStatementDaysAgo: 14,
  },
  {
    name: 'Cedar Valley Foods',
    code: 'CDRV',
    domain: 'cedarvalleyfoods.com',
    apName: 'Devon Fairbanks',
    invoiceCount: 4,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Ironclad Fasteners',
    code: 'IRON',
    domain: 'ironcladfasteners.com',
    apName: 'Priscilla Yoon',
    invoiceCount: 2,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Brightwater Beverage Co.',
    code: 'BRWT',
    domain: 'brightwaterbev.com',
    apName: 'Theo Marchetti',
    invoiceCount: 2,
    lastStatementDaysAgo: 21,
  },
  {
    name: 'Coastal Logistics Group',
    code: 'CSTL',
    domain: 'coastallogistics.com',
    apName: 'Renata Cho',
    invoiceCount: 3,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Maple Crest Hardware',
    code: 'MPLC',
    domain: 'maplecresthardware.com',
    apName: 'Gideon Park',
    invoiceCount: 1,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Atlas Packaging Solutions',
    code: 'ATLS',
    domain: 'atlaspackaging.com',
    apName: 'Saanvi Rao',
    invoiceCount: 2,
    lastStatementDaysAgo: 14,
  },
  {
    name: 'Riverbend Manufacturing',
    code: 'RVBD',
    domain: 'riverbendmfg.com',
    apName: 'Colton Reyes',
    invoiceCount: 4,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Silverline Electronics',
    code: 'SLVR',
    domain: 'silverlineelec.com',
    apName: 'Noor Haddad',
    invoiceCount: 2,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Granite State Tooling',
    code: 'GRST',
    domain: 'granitestatetooling.com',
    apName: 'Wes Donnelly',
    invoiceCount: 1,
    lastStatementDaysAgo: 28,
  },
  {
    name: 'Pinewood Distributors',
    code: 'PNWD',
    domain: 'pinewooddist.com',
    apName: 'Amara Beckett',
    invoiceCount: 3,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Vanguard Office Supply',
    code: 'VNGD',
    domain: 'vanguardoffice.com',
    apName: 'Felix Aguirre',
    invoiceCount: 2,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Lakeshore Chemicals',
    code: 'LKSH',
    domain: 'lakeshorechem.com',
    apName: 'Talia Brennan',
    invoiceCount: 2,
    lastStatementDaysAgo: 14,
  },
  {
    name: 'Copperfield Components',
    code: 'CPFD',
    domain: 'copperfieldco.com',
    apName: 'Marcus Webb',
    invoiceCount: 3,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Sunfield Agriculture',
    code: 'SNFD',
    domain: 'sunfieldag.com',
    apName: 'Indira Vasquez',
    invoiceCount: 1,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Beacon Hill Textiles',
    code: 'BCNH',
    domain: 'beaconhilltextiles.com',
    apName: 'Quentin Frost',
    invoiceCount: 2,
    lastStatementDaysAgo: 21,
  },
  {
    name: 'Trailhead Outdoor Gear',
    code: 'TRHD',
    domain: 'trailheadgear.com',
    apName: 'Lena Sokolov',
    invoiceCount: 4,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Meridian Medical Supply',
    code: 'MRDN',
    domain: 'meridianmed.com',
    apName: 'Dr. Asa Klein',
    invoiceCount: 2,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Foundry Row Metals',
    code: 'FNDR',
    domain: 'foundryrowmetals.com',
    apName: 'Hana Okafor',
    invoiceCount: 3,
    lastStatementDaysAgo: 14,
  },
  {
    name: 'Clearview Glassworks',
    code: 'CLRV',
    domain: 'clearviewglass.com',
    apName: 'Sebastian Nguyen',
    invoiceCount: 1,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Highline Construction Supply',
    code: 'HGLN',
    domain: 'highlinesupply.com',
    apName: 'Camille Dubois',
    invoiceCount: 2,
    lastStatementDaysAgo: null,
  },
  {
    name: 'Birchwood Paper Co.',
    code: 'BRCH',
    domain: 'birchwoodpaper.com',
    apName: 'Roman Petrov',
    invoiceCount: 3,
    lastStatementDaysAgo: 7,
  },
  {
    name: 'Tidewater Marine Parts',
    code: 'TDWR',
    domain: 'tidewatermarine.com',
    apName: 'Joelle Ferraro',
    invoiceCount: 2,
    lastStatementDaysAgo: 35,
  },
];

/**
 * Deterministic per-invoice amount for customer index `ci`, invoice index `ii`.
 * Spread across a believable B2B range ($3k–$48k) without any randomness.
 */
function invoiceAmount(ci: number, ii: number): number {
  const base = 3200 + ((ci * 1700 + ii * 4300) % 44000);
  // Round to cents on a "nice-ish" boundary.
  return Math.round(base / 5) * 5;
}

/** Build the open invoices for one customer (all overdue, oldest first). */
function buildInvoices(
  spec: BulkCustomerSpec,
  ci: number,
): ArInvoiceListItem[] {
  const customerId = `demo-bulk-cust-${spec.code}`;
  return Array.from({ length: spec.invoiceCount }).map((_unused, ii) => {
    // Oldest invoice ~75 days out, newest ~32 days out — all past a Net 30 due.
    const invoicedDaysAgo = 75 - ii * 14 - (ci % 5);
    const amount = invoiceAmount(ci, ii).toFixed(2);
    const sentDaysAgo = invoicedDaysAgo - 1;
    return {
      id: `demo-bulk-inv-${spec.code}-${ii + 1}`,
      invoiceNumber: `INV-${spec.code}-${String(4100 + ci * 7 + ii).padStart(5, '0')}`,
      invoiceDate: dateOnlyDaysFromNow(-invoicedDaysAgo),
      dueDate: dateOnlyDaysFromNow(-invoicedDaysAgo + 30),
      status: 'open',
      grossAmount: amount,
      netAmount: amount,
      balanceDue: amount,
      currencyCode: 'USD',
      customerName: spec.name,
      customerId,
      customerPurchaseOrderReference: `PO-${spec.code}-${2000 + ci * 3 + ii}`,
      salesOrderId: `demo-bulk-so-${spec.code}-${ii + 1}`,
      shipDate: dateOnlyDaysFromNow(-invoicedDaysAgo - 2),
      paymentApplicationCount: 0,
      firstSentAt: isoDaysFromNow(-sentDaysAgo),
      sendEvents: [
        {
          occurredAt: isoDaysFromNow(-sentDaysAgo),
          note: `Invoice emailed to ap@${spec.domain}`,
        },
      ],
    } satisfies ArInvoiceListItem;
  });
}

/** Sum decimal-string balances → a decimal string. */
function sumBalances(invoices: ArInvoiceListItem[]): string {
  const total = invoices.reduce(
    (acc, inv) => acc + Number(inv.balanceDue ?? '0'),
    0,
  );
  return total.toFixed(2);
}

/** The default branded statement email body (HTML) for one customer. */
function buildHtmlBody(spec: BulkCustomerSpec, overdue: string): string {
  return [
    `<p>Hello ${spec.name} team,</p>`,
    `<p>Please find attached your current statement of outstanding invoices with ${ORG_NAME}. Our records show an outstanding balance of <strong>${usd(overdue)}</strong>.</p>`,
    `<p>Kindly arrange payment at your earliest convenience. If any of these invoices have already been paid or you have questions about a charge, just reply to this email and we'll be glad to help.</p>`,
    `<p>Thank you for your business,<br/>${ORG_NAME} Accounts Receivable</p>`,
  ].join('\n');
}

function buildContacts(spec: BulkCustomerSpec): ArCustomerContact[] {
  const customerId = `demo-bulk-cust-${spec.code}`;
  const contacts: ArCustomerContact[] = [
    {
      id: `demo-bulk-contact-${spec.code}-ap`,
      name: spec.apName,
      email: `ap@${spec.domain}`,
      phone: null,
      title: 'Accounts Payable',
      isPrimary: true,
      originMetadata: null,
      tags: [{ id: `demo-bulk-tag-${spec.code}-ap`, name: 'AP' }],
    },
    {
      id: `demo-bulk-contact-${spec.code}-billing`,
      name: 'Billing Department',
      email: `billing@${spec.domain}`,
      phone: null,
      title: null,
      isPrimary: false,
      originMetadata: null,
      tags: [{ id: `demo-bulk-tag-${spec.code}-billing`, name: 'Billing' }],
    },
  ];
  void customerId;
  return contacts;
}

/** Build the full bulk-statement slice (25 customers + per-customer payloads). */
export function buildBulkStatementSeed(): DemoBulkStatementState {
  const customers: DemoBulkStatementState['customers'] = [];
  const previewByCustomer: Record<string, ArStatementEmailPreviewOutput> = {};
  const defaultsByCustomer: DemoBulkStatementState['defaultsByCustomer'] = {};
  const contactsByCustomer: DemoBulkStatementState['contactsByCustomer'] = {};

  CUSTOMERS.forEach((spec, ci) => {
    const customerId = `demo-bulk-cust-${spec.code}`;
    const invoices = buildInvoices(spec, ci);
    const overdue = sumBalances(invoices);
    const apEmail = `ap@${spec.domain}`;

    customers.push({
      customerId,
      customerName: spec.name,
      openBalance: overdue,
      lastStatementSentAt:
        spec.lastStatementDaysAgo === null
          ? null
          : isoDaysFromNow(-spec.lastStatementDaysAgo),
    });

    previewByCustomer[customerId] = {
      from: SENDER_EMAIL,
      to: apEmail,
      cc: [],
      bcc: [],
      subject: `Statement of Outstanding Invoices — ${spec.name}`,
      htmlContent: buildHtmlBody(spec, overdue),
      // Rendered on demand by the preview shim from `invoices` (DemoStatementPdf).
      pdfBase64: '',
      pdfFilename: `statement-${spec.code}-${dateOnlyDaysFromNow(0)}.pdf`,
      invoices,
      templateVariables: {
        customer_name: spec.name,
        organization_name: ORG_NAME,
        total_overdue: usd(overdue),
        invoice_count: String(invoices.length),
        statement_date: dateOnlyDaysFromNow(0),
      },
    };

    defaultsByCustomer[customerId] = {
      defaultRecipientEmails: [apEmail],
      // Don't BCC the sender in the demo — keeps the presenter's real personal
      // address out of the recipients/summary.
      bccSender: false,
      sendFrom: { mode: 'configured' },
    };

    contactsByCustomer[customerId] = buildContacts(spec);
  });

  return {
    open: false,
    inboxActionId: BULK_STATEMENT_ACTION_ID,
    customers,
    previewByCustomer,
    defaultsByCustomer,
    contactsByCustomer,
  };
}

/** Total overdue across the batch, decimal string — for the inbox task amount. */
export function bulkStatementBatchTotal(slice: DemoBulkStatementState): string {
  const total = slice.customers.reduce(
    (acc, c) => acc + Number(c.openBalance ?? '0'),
    0,
  );
  return total.toFixed(2);
}

// ---------------------------------------------------------------------------
// Aging / custom-aging derivation. The same 25-customer batch the bulk-send
// dialog shows also has to appear in the AR summary aging detail (useArAging →
// `aging`) and the Reports flow (useArCustomAging → `customAging`), so the book
// you can send statements to matches the book you can age. Everything below is
// derived from the already-built bulk slice so the numbers stay identical.
// ---------------------------------------------------------------------------

/** Sum decimal-string amounts → a 2-dp decimal string (cent-accurate). */
function sumDec(values: Array<string | null>): string {
  const cents = values.reduce(
    (acc, v) => acc + Math.round(parseFloat(v ?? '0') * 100),
    0,
  );
  return (cents / 100).toFixed(2);
}

/** Whole days a date-only `dueDate` ("YYYY-MM-DD") is past today (UTC). */
function daysOverdueFromDueDate(dueDate: string): number {
  const due = Date.parse(`${dueDate}T00:00:00Z`);
  const now = new Date();
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return Math.floor((today - due) / 86_400_000);
}

/** Aging-bucket index (0 Current, 1 1-30, 2 31-60, 3 61-90, 4 90+). */
function bucketIndexForDaysOverdue(days: number): number {
  if (days <= 0) return 0;
  if (days <= 30) return 1;
  if (days <= 60) return 2;
  if (days <= 90) return 3;
  return 4;
}

/** Per-customer aging rows for the batch (bucket amounts from each invoice). */
function buildBulkAgingCustomers(
  slice: DemoBulkStatementState,
): GetArAgingResponse['customers'] {
  return slice.customers.map((c) => {
    const invoices = slice.previewByCustomer[c.customerId]?.invoices ?? [];
    const cents = [0, 0, 0, 0, 0];
    for (const inv of invoices) {
      // Batch invoices always carry a dueDate; treat a missing one as current.
      const idx = inv.dueDate
        ? bucketIndexForDaysOverdue(daysOverdueFromDueDate(inv.dueDate))
        : 0;
      cents[idx] += Math.round(parseFloat(inv.balanceDue ?? '0') * 100);
    }
    const dec = (n: number) => (n / 100).toFixed(2);
    const total = cents.reduce((a, b) => a + b, 0);
    return {
      customerId: c.customerId,
      customerName: c.customerName,
      current: dec(cents[0]),
      days1_30: dec(cents[1]),
      days31_60: dec(cents[2]),
      days61_90: dec(cents[3]),
      days90plus: dec(cents[4]),
      bucketAmounts: cents.map(dec),
      totalOutstanding: dec(total),
      totalApplied: '0.00',
      invoiceCount: invoices.length,
    };
  });
}

/** Per-customer grouped rows for the Reports custom-aging table. */
function buildBulkCustomAgingCustomers(
  slice: DemoBulkStatementState,
): GetArCustomAgingResponse['customers'] {
  return slice.customers.map((c) => ({
    customerId: c.customerId,
    customerName: c.customerName,
    openBalance: c.openBalance ?? '0.00',
    invoiceCount: slice.previewByCustomer[c.customerId]?.invoices.length ?? 0,
    lastStatementSentAt: c.lastStatementSentAt,
  }));
}

/** Flat list of every batch invoice (for the Reports flat/invoice view). */
function buildBulkReportInvoices(
  slice: DemoBulkStatementState,
): ArInvoiceListItem[] {
  return slice.customers.flatMap(
    (c) => slice.previewByCustomer[c.customerId]?.invoices ?? [],
  );
}

/** Append the batch's customers (with recomputed totals) to an aging response. */
export function appendBulkToAging(
  base: GetArAgingResponse,
  slice: DemoBulkStatementState,
): GetArAgingResponse {
  const customers = [...base.customers, ...buildBulkAgingCustomers(slice)];
  const bucket = (i: number) =>
    sumDec(customers.map((c) => c.bucketAmounts[i] ?? '0'));
  return {
    ...base,
    customers,
    totals: {
      current: bucket(0),
      days1_30: bucket(1),
      days31_60: bucket(2),
      days61_90: bucket(3),
      days90plus: bucket(4),
      bucketAmounts: [bucket(0), bucket(1), bucket(2), bucket(3), bucket(4)],
      totalOutstanding: sumDec(customers.map((c) => c.totalOutstanding)),
      totalApplied: sumDec(customers.map((c) => c.totalApplied)),
    },
  };
}

/** Append the batch's customers + invoices to a custom-aging response. */
export function appendBulkToCustomAging(
  base: GetArCustomAgingResponse,
  slice: DemoBulkStatementState,
): GetArCustomAgingResponse {
  const customers = [
    ...base.customers,
    ...buildBulkCustomAgingCustomers(slice),
  ];
  const invoices = [...base.invoices, ...buildBulkReportInvoices(slice)];
  return {
    customers,
    invoices,
    totals: {
      openBalance: sumDec(customers.map((c) => c.openBalance ?? '0')),
      invoiceCount: invoices.length,
    },
  };
}
